import React, { useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';
import { ArchiveRounded as ArchiveIcon, UnarchiveRounded as UnarchiveIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetProductById } from '../../hooks/admin-get-product-by-id.hook';
import { useUpdateProduct } from '../../hooks/update-product.hook';
import { useArchiveProduct } from '../../hooks/archive-product.hook';
import { useRestoreProduct } from '../../hooks/restore-product.hook';
import { UpdateProductRequestDTO } from '../../types/dtos/update-product.dto';
import ProductInformationsTab from '../../components/Containers/ProductInformationsTab.component';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

type ArchiveAction = 'archive' | 'restore';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: ArchiveAction };

const CONFIRM_DIALOG_CONTENT: Record<ArchiveAction, { title: string; description: string; confirmLabel: string }> = {
  archive: {
    title: 'Archiver le produit',
    description: 'Le produit sera retiré des listes et du site, et pourra être restauré.',
    confirmLabel: 'Archiver',
  },
  restore: {
    title: 'Restaurer le produit',
    description: 'Le produit retrouvera sa place en fin de sous-rubrique, inactif.',
    confirmLabel: 'Restaurer',
  },
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',

    '& > h2': {
      margin: 0,
    },
  },

  archiveButton: {
    marginLeft: 'auto',
    textTransform: 'none',
  },
});

export default function AdminProductDetailsView() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { productId } = useParams<{ productId: string }>();

  const params = new URLSearchParams(location.search);
  const currentTab = params.get('tab') || 'informations';

  const { isLoading, product, refetch } = useAdminGetProductById({ productId });
  const { updateProduct, isSubmitting } = useUpdateProduct();
  const { archiveProduct, isSubmitting: isArchiving } = useArchiveProduct();
  const { restoreProduct, isSubmitting: isRestoring } = useRestoreProduct();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const isArchived = Boolean(product?.archivedAt);

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleProductUpdated = async (productData: UpdateProductRequestDTO) => {
    if (isLoading || !product) return;

    const updatedProduct = await updateProduct({ productId: product.id, productData });
    if (!updatedProduct) return;

    await refetch();
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !product) return;

    const result = dialog.action === 'archive' ? await archiveProduct(product.id) : await restoreProduct(product.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && product && (
        <React.Fragment>
          <div className={classes.header}>
            <h2>{product.name}</h2>
            <StatusChip isActive={product.isActive} archived={isArchived} />
            <Button
              size="small"
              variant="outlined"
              className={classes.archiveButton}
              startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
              onClick={() => setDialog({ kind: 'confirm', action: isArchived ? 'restore' : 'archive' })}
            >
              {isArchived ? 'Restaurer' : 'Archiver'}
            </Button>
          </div>
          <TabsController
            size="small"
            tabs={[
              { id: 'statistics', label: 'Statistiques', value: 'statistics', disabled: true },
              { id: 'informations', label: 'Informations', value: 'informations' },
            ]}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'informations' && (
            <ProductInformationsTab
              product={product}
              isSubmitting={isSubmitting}
              updateProduct={handleProductUpdated}
              refreshProduct={refetch}
            />
          )}
        </React.Fragment>
      )}
      {dialog.kind === 'confirm' && (
        <ConfirmActionDialog
          open
          title={CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={CONFIRM_DIALOG_CONTENT[dialog.action].description}
          confirmLabel={CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
